import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { OrgPrintHeader } from './OrgPrintChrome';
import { saveBlobResponse } from '../utils/download';

import DateInput from './DateInput';
const VIEWS = [
  { key: 'pl', label: 'Profit & Loss' },
  { key: 'bs', label: 'Balance Sheet' },
  { key: 'tb', label: 'Trial Balance' },
  { key: 'daybook', label: 'Day Book' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'registers', label: 'Registers' }
];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Indian financial years run April to March. */
const fyStart = (d) => new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);

const PRESETS = [
  { key: 'this-fy', label: 'This financial year' },
  { key: 'last-fy', label: 'Last financial year' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'custom', label: 'Custom dates' }
];

const presetPeriod = (key) => {
  const today = new Date();
  switch (key) {
    case 'last-fy': {
      const start = fyStart(today);
      return { from: iso(new Date(start.getFullYear() - 1, 3, 1)), to: iso(new Date(start.getFullYear(), 2, 31)) };
    }
    case 'this-month':
      return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    case 'last-month':
      return {
        from: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to: iso(new Date(today.getFullYear(), today.getMonth(), 0))
      };
    default:
      return { from: iso(fyStart(today)), to: iso(today) };
  }
};

const fyLabel = (from) => {
  const start = fyStart(new Date(`${from}T00:00:00`));
  return `FY ${start.getFullYear()}-${String((start.getFullYear() + 1) % 100).padStart(2, '0')}`;
};

const showDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const typeLabel = (t) => (t ? t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' ').replace(/\bitc\b/g, 'ITC').replace(/\brcm\b/g, 'RCM') : '');

const csvCell = (v) => {
  let t = v === null || v === undefined ? '' : String(v);
  if (/^[=+\-@]/.test(t) && !/^-?\d/.test(t)) t = `'${t}`;
  return `"${t.replace(/"/g, '""')}"`;
};

const saveCsv = (name, rows) => {
  const blob = new Blob([`﻿${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Profit & Loss, Balance Sheet, Trial Balance and Day Book, read from the ledgers for a period (Balance Sheet: as at its
 * last day), one ledger's statement (a customer's or vendor's ledger for the accountant or the party), and the sales and
 * purchase registers to download. Stock in the Profit & Loss and Balance Sheet is valued from the stock records; see
 * FinancialStatementsService. ledgerRequest ({ code }) opens a ledger's statement, from Receivables or Payables.
 */
function FinancialStatements({ active, currency = 'INR', ledgerRequest }) {
  const [view, setView] = useState('pl');
  const [ledgers, setLedgers] = useState(null);
  const [ledgerId, setLedgerId] = useState('');
  const [wantedCode, setWantedCode] = useState(null);
  const [preset, setPreset] = useState('this-fy');
  const [period, setPeriod] = useState(presetPeriod('this-fy'));
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState('');

  const formatter = useMemo(() => new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [currency]);
  const money = useCallback((v) => formatter.format(Number(v || 0)), [formatter]);
  const drCr = useCallback((v) => {
    const n = Number(v || 0);
    if (n === 0) return '-';
    return `${money(Math.abs(n))} ${n > 0 ? 'Dr' : 'Cr'}`;
  }, [money]);

  // A request from Receivables or Payables: that party's ledger
  useEffect(() => {
    if (ledgerRequest?.code) {
      setView('ledger');
      setWantedCode(ledgerRequest.code);
    }
  }, [ledgerRequest]);

  useEffect(() => {
    if (!active || view !== 'ledger' || ledgers) return;
    axios.get('/api/financial-statements/ledgers')
      .then((r) => setLedgers(r.data))
      .catch(() => toast.error('Could not load the ledgers'));
  }, [active, view, ledgers]);

  useEffect(() => {
    if (!wantedCode || !ledgers) return;
    const found = ledgers.find((l) => l.code === wantedCode);
    if (found) setLedgerId(String(found.id));
    else toast.warn('That ledger has no entries yet');
    setWantedCode(null);
  }, [wantedCode, ledgers]);

  const openLedger = (id) => {
    setLedgerId(String(id));
    setView('ledger');
    setPage(0);
  };

  const load = useCallback(async () => {
    if (!period.from || !period.to) return;
    if (view === 'registers') {
      setData({ view });
      setProblem('');
      return;
    }
    if (view === 'ledger' && !ledgerId) {
      setData(null);
      setProblem('');
      return;
    }
    const url = {
      pl: `/api/financial-statements/profit-and-loss?from=${period.from}&to=${period.to}`,
      bs: `/api/financial-statements/balance-sheet?asOf=${period.to}`,
      tb: `/api/financial-statements/trial-balance?from=${period.from}&to=${period.to}`,
      daybook: `/api/financial-statements/day-book?from=${period.from}&to=${period.to}&page=${page}&size=200`,
      ledger: `/api/financial-statements/ledger-statement?ledgerId=${ledgerId}&from=${period.from}&to=${period.to}`
    }[view];
    try {
      setLoading(true);
      setProblem('');
      const response = await axios.get(url);
      setData({ view, ...response.data });
    } catch (error) {
      setData(null);
      const msg = typeof error.response?.data === 'string' && error.response.data ? error.response.data : 'Could not prepare the statement';
      setProblem(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [view, period, page, ledgerId]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const choosePreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setPeriod(presetPeriod(key));
    setPage(0);
  };

  const shown = data && data.view === view && (view !== 'ledger' || String(data.ledgerId) === ledgerId) ? data : null;
  const title = view === 'ledger' ? (shown ? shown.name : 'Ledger') : VIEWS.find((v) => v.key === view).label;
  const periodText = view === 'bs'
    ? `As at ${showDate(period.to)}`
    : `${showDate(period.from)} to ${showDate(period.to)}`;

  const downloadCsv = async () => {
    if (!shown) return;
    const name = `${title.toLowerCase().replace(/[^a-z]+/g, '-')}-${view === 'bs' ? period.to : `${period.from}-to-${period.to}`}.csv`;
    if (view === 'pl') {
      saveCsv(name, [
        ['Profit & Loss', periodText], [],
        ['Sales'], ...shown.sales.map((l) => ['', l.name, l.amount]), ['', 'Total sales', shown.salesTotal], [],
        ['Cost of goods sold'], ['', 'Opening stock', shown.openingStock],
        ...shown.purchases.map((l) => ['', `Add: ${l.name}`, l.amount]),
        ['', 'Less: Closing stock', shown.closingStock], ['', 'Cost of goods sold', shown.costOfGoodsSold], [],
        ['Gross profit', '', shown.grossProfit], [],
        ['Other income'], ...shown.otherIncome.map((l) => ['', l.name, l.amount]), [],
        ['Expenses'], ...shown.expenses.map((l) => ['', l.name, l.amount]), ['', 'Total expenses', shown.expensesTotal], [],
        ['Net profit', '', shown.netProfit]
      ]);
    } else if (view === 'bs') {
      const side = (groups) => groups.flatMap((g) => [[g.group, '', g.amount], ...g.ledgers.map((l) => ['', l.name, l.amount])]);
      saveCsv(name, [
        ['Balance Sheet', periodText], [],
        ['Capital and liabilities'], ...side(shown.liabilities), ['Total', '', shown.totalLiabilities], [],
        ['Assets'], ...side(shown.assets), ['Total', '', shown.totalAssets]
      ]);
    } else if (view === 'tb') {
      saveCsv(name, [
        ['Ledger', 'Group', 'Opening (Dr +, Cr -)', 'Debit', 'Credit', 'Closing (Dr +, Cr -)'],
        ...shown.rows.map((r) => [r.name, r.group, r.opening, r.debit, r.credit, r.closing]),
        ['Total', '', '', shown.debit, shown.credit, '']
      ]);
    } else if (view === 'ledger') {
      const party = shown.party;
      const amt = (v) => (Number(v) ? Number(v).toFixed(2) : '');
      const bal = (v) => [Math.abs(Number(v)).toFixed(2), Number(v) > 0 ? 'Dr' : Number(v) < 0 ? 'Cr' : ''];
      saveCsv(`ledger-${(shown.code || shown.name).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${period.from}-to-${period.to}.csv`, [
        [party ? party.name : shown.name, periodText],
        ...(party?.gstin ? [['GSTIN', party.gstin]] : []),
        ...(party?.address ? [['Address', party.address]] : []),
        [],
        ['Date', 'Reference', 'Particulars', 'Entry', 'Debit', 'Credit', 'Balance', 'Dr/Cr'],
        ['', '', 'Opening balance', '', '', '', ...bal(shown.opening)],
        ...shown.entries.map((e) => [String(e.date).slice(0, 10), e.reference, [e.against, e.description].filter(Boolean).join(': '),
          typeLabel(e.type), amt(e.debit), amt(e.credit), ...bal(e.balance)]),
        ['', '', 'Total', '', amt(shown.totalDebit), amt(shown.totalCredit), '', ''],
        ['', '', 'Closing balance', '', '', '', ...bal(shown.closing)]
      ]);
    } else {
      // The whole period, not only the page on screen
      let entries = shown.entries;
      if (shown.pages > 1) {
        try {
          const all = await axios.get(`/api/financial-statements/day-book?from=${period.from}&to=${period.to}&page=0&size=5000`);
          entries = all.data.entries;
          if (all.data.pages > 1) toast.warn('The download has the first 5,000 entries; choose a shorter period for the rest.');
        } catch (error) {
          toast.error('Could not download the day book');
          return;
        }
      }
      saveCsv(name, [
        ['Date', 'Reference', 'Type', 'Debit ledger', 'Credit ledger', 'Amount', 'Description'],
        ...entries.map((e) => [String(e.date).slice(0, 10), e.reference, typeLabel(e.type), e.debitLedger, e.creditLedger, e.amount, e.description])
      ]);
    }
  };

  return (
    <div className="mt-3">
      <Card className="mb-3 d-print-none">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col xs={12} lg="auto">
              <ButtonGroup size="sm" className="flex-wrap">
                {VIEWS.map((v) => (
                  <Button key={v.key} variant={view === v.key ? 'primary' : 'outline-primary'}
                    onClick={() => { setView(v.key); setPage(0); }}>{v.label}</Button>
                ))}
              </ButtonGroup>
            </Col>
            {view === 'ledger' && (
              <Col xs={12} sm="auto">
                <Form.Label className="small mb-1">Ledger</Form.Label>
                <Form.Select size="sm" value={ledgerId} onChange={(e) => setLedgerId(e.target.value)} style={{ maxWidth: '22rem' }}>
                  <option value="">{ledgers ? 'Choose a ledger' : 'Loading...'}</option>
                  {ledgerGroups(ledgers).map(([group, list]) => (
                    <optgroup key={group} label={group}>
                      {list.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </optgroup>
                  ))}
                </Form.Select>
              </Col>
            )}
            <Col xs={12} sm="auto">
              <Form.Label className="small mb-1">Period</Form.Label>
              <Form.Select size="sm" value={preset} onChange={(e) => choosePreset(e.target.value)}>
                {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Form.Select>
            </Col>
            {view !== 'bs' && (
              <Col xs={6} sm="auto">
                <Form.Label className="small mb-1">From</Form.Label>
                <DateInput size="sm" value={period.from}
                  onChange={(e) => { setPreset('custom'); setPage(0); setPeriod((p) => ({ ...p, from: e.target.value })); }} />
              </Col>
            )}
            <Col xs={6} sm="auto">
              <Form.Label className="small mb-1">{view === 'bs' ? 'As at' : 'To'}</Form.Label>
              <DateInput size="sm" value={period.to}
                onChange={(e) => { setPreset('custom'); setPage(0); setPeriod((p) => ({ ...p, to: e.target.value })); }} />
            </Col>
            {view !== 'registers' && (
              <Col xs={12} sm className="d-flex gap-2 justify-content-sm-end">
                <Button size="sm" variant="outline-secondary" className="text-nowrap" onClick={downloadCsv} disabled={!shown}>Download CSV</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => window.print()} disabled={!shown}>Print</Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {problem && !loading && <Alert variant="danger" className="d-print-none">{problem}</Alert>}
      {loading && !shown && <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>}

      {view === 'ledger' && !ledgerId && !loading && (
        <p className="text-muted small">
          Choose a ledger: a customer (Sundry Debtors), a vendor (Sundry Creditors), a bank, or any other. You can also click a
          ledger in the Trial Balance.
        </p>
      )}

      {view === 'registers' && <RegistersView period={period} periodText={periodText} />}

      {shown && view !== 'registers' && (
        <div className="print-modal">
          <div className="d-none d-print-block"><OrgPrintHeader /></div>
          <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
            <h5 className="mb-0">{view === 'ledger' && shown.party ? shown.party.name : title}</h5>
            <span className="text-muted small">{periodText}{view !== 'bs' && view !== 'daybook' ? ` (${fyLabel(period.from)})` : ''}</span>
            {loading && <Spinner animation="border" size="sm" className="d-print-none" />}
          </div>
          {view === 'pl' && <ProfitAndLossView pl={shown} money={money} />}
          {view === 'bs' && <BalanceSheetView bs={shown} money={money} />}
          {view === 'tb' && <TrialBalanceView tb={shown} money={money} drCr={drCr} onLedger={openLedger} />}
          {view === 'ledger' && <LedgerView st={shown} money={money} drCr={drCr} />}
          {view === 'daybook' && <DayBookView book={shown} money={money} page={page} setPage={setPage} />}
          {(view === 'pl' || view === 'bs') && (
            <p className="text-muted small mt-2 mb-0">
              Stock is valued lot by lot from goods receipts and issues, at the cost posted to the books (the goods&apos; value plus
              services such as handling or transport allocated to them).
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Amount({ value, money, strong }) {
  const n = Number(value || 0);
  const text = n < 0 ? `(${money(-n)})` : money(n);
  return <td className={`text-end text-nowrap${strong ? ' fw-semibold' : ''}`}>{text}</td>;
}

function ProfitAndLossView({ pl, money }) {
  const section = (label) => <tr className="statement-head"><th colSpan={2}>{label}</th></tr>;
  const lines = (list, prefix = '') => list.map((l) => (
    <tr key={`${prefix}${l.name}`}><td className="ps-4">{prefix}{l.name}</td><Amount value={l.amount} money={money} /></tr>
  ));
  return (
    <>
    {Number(pl.costOfGoodsSold) < 0 && (
      <Alert variant="warning" className="small py-2 d-print-none">
        <strong>Closing stock is worth more than opening stock plus goods bought</strong>, so the cost of goods sold is negative
        and the profit is too high. Goods came into stock at a value that wasn&apos;t bought: usually production output or
        returns received at more than went into them, or goods sent back to vendors on a return note without being issued
        from their lot. Check those goods receipts and issues in Inventory.
      </Alert>
    )}
    <Table size="sm" bordered className="statement-table">
      <tbody>
        {section('Sales')}
        {pl.sales.length === 0 && <tr><td className="ps-4 text-muted">No sales in this period</td><Amount value={0} money={money} /></tr>}
        {lines(pl.sales)}
        <tr><td className="fw-semibold">Total sales</td><Amount value={pl.salesTotal} money={money} strong /></tr>

        {section('Cost of goods sold')}
        <tr><td className="ps-4">Opening stock</td><Amount value={pl.openingStock} money={money} /></tr>
        {lines(pl.purchases, 'Add: ')}
        <tr><td className="ps-4">Less: Closing stock</td><Amount value={-Number(pl.closingStock)} money={money} /></tr>
        <tr><td className="fw-semibold">Cost of goods sold</td><Amount value={pl.costOfGoodsSold} money={money} strong /></tr>

        <tr className="statement-total"><th>Gross profit</th><Amount value={pl.grossProfit} money={money} strong /></tr>

        {pl.otherIncome.length > 0 && (
          <>
            {section('Other income')}
            {lines(pl.otherIncome)}
            <tr><td className="fw-semibold">Total other income</td><Amount value={pl.otherIncomeTotal} money={money} strong /></tr>
          </>
        )}
        {section('Expenses')}
        {pl.expenses.length === 0 && <tr><td className="ps-4 text-muted">No expenses in this period</td><Amount value={0} money={money} /></tr>}
        {lines(pl.expenses)}
        <tr><td className="fw-semibold">Total expenses</td><Amount value={pl.expensesTotal} money={money} strong /></tr>

        <tr className={`statement-total ${Number(pl.netProfit) >= 0 ? 'statement-profit' : 'statement-loss'}`}>
          <th>{Number(pl.netProfit) >= 0 ? 'Net profit' : 'Net loss'}</th>
          <Amount value={Math.abs(Number(pl.netProfit))} money={money} strong />
        </tr>
      </tbody>
    </Table>
    </>
  );
}

function BalanceSheetView({ bs, money }) {
  const side = (title, groups, total) => (
    <Table size="sm" bordered className="statement-table mb-0">
      <thead><tr className="statement-head"><th>{title}</th><th className="text-end">Amount</th></tr></thead>
      <tbody>
        {groups.map((g) => (
          <GroupRows key={g.group} group={g} money={money} />
        ))}
        {groups.length === 0 && <tr><td className="text-muted">Nothing yet</td><Amount value={0} money={money} /></tr>}
      </tbody>
      <tfoot><tr className="statement-total"><th>Total</th><Amount value={total} money={money} strong /></tr></tfoot>
    </Table>
  );
  const difference = Number(bs.openingDifference || 0);
  return (
    <>
      {Math.abs(difference) >= 0.005 && (
        <Alert variant="warning" className="small py-2">
          <strong>Difference in opening balances: {money(Math.abs(difference))}.</strong> Opening balances entered on ledgers
          don&apos;t net to zero, so the two sides differ by that much. Check the opening balances in Accounts &rarr; Ledgers.
        </Alert>
      )}
      <Row className="g-3">
        <Col md={6}>{side('Capital and liabilities', bs.liabilities, bs.totalLiabilities)}</Col>
        <Col md={6}>{side('Assets', bs.assets, bs.totalAssets)}</Col>
      </Row>
      <p className="text-muted small mt-2 mb-0">
        Profit &amp; Loss A/c: {money(bs.profitBroughtForward)} brought forward to {showDate(bs.yearStart)}, and{' '}
        {money(bs.profitThisYear)} {Number(bs.profitThisYear) >= 0 ? 'profit' : 'loss'} this financial year.
      </p>
    </>
  );
}

function GroupRows({ group, money }) {
  const [open, setOpen] = useState(false);
  const many = group.ledgers.length > 1 || (group.ledgers[0] && group.ledgers[0].name !== group.group);
  return (
    <>
      <tr>
        <td>
          {many ? (
            <button type="button" className="btn btn-link btn-sm p-0 text-reset text-decoration-none" onClick={() => setOpen(!open)}
              aria-expanded={open}>
              <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-1 small d-print-none`} />{group.group}
            </button>
          ) : group.group}
        </td>
        <Amount value={group.amount} money={money} strong />
      </tr>
      {group.ledgers.map((l) => (
        <tr key={l.name} className={open ? '' : 'd-none d-print-table-row'}>
          <td className="ps-4 small text-muted">{l.name}</td>
          <td className="text-end small text-muted text-nowrap">{Number(l.amount) < 0 ? `(${money(-Number(l.amount))})` : money(l.amount)}</td>
        </tr>
      ))}
    </>
  );
}

/** The ledgers in their groups, in the order the server gives (statement order). */
const ledgerGroups = (ledgers) => {
  const groups = new Map();
  (ledgers || []).forEach((l) => {
    if (!groups.has(l.group)) groups.set(l.group, []);
    groups.get(l.group).push(l);
  });
  return [...groups.entries()];
};

function LedgerView({ st, money, drCr }) {
  const party = st.party;
  return (
    <>
      {party && (
        <div className="small mb-2">
          {party.kind === 'CUSTOMER' ? 'Customer' : 'Vendor'} ledger{party.gstin ? <> &middot; GSTIN {party.gstin}</> : null}
          {party.address ? <> &middot; {party.address}</> : null}
        </div>
      )}
      <Table size="sm" bordered hover responsive className="statement-table">
        <thead className="statement-head">
          <tr><th>Date</th><th>Reference</th><th>Particulars</th><th className="text-end">Debit</th>
            <th className="text-end">Credit</th><th className="text-end">Balance</th></tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={3} className="fw-semibold">Opening balance</td><td /><td />
            <td className="text-end text-nowrap fw-semibold">{drCr(st.opening)}</td>
          </tr>
          {st.entries.map((e) => (
            <tr key={e.id}>
              <td className="text-nowrap">{showDate(e.date)}</td>
              <td className="text-nowrap">{e.reference}</td>
              <td className="small">
                {e.against}
                <div className="text-muted">{typeLabel(e.type)}{e.description ? `: ${e.description}` : ''}</div>
              </td>
              <td className="text-end text-nowrap">{Number(e.debit) ? money(e.debit) : ''}</td>
              <td className="text-end text-nowrap">{Number(e.credit) ? money(e.credit) : ''}</td>
              <td className="text-end text-nowrap">{drCr(e.balance)}</td>
            </tr>
          ))}
          {st.entries.length === 0 && <tr><td colSpan={6} className="text-center text-muted">No entries in this period.</td></tr>}
        </tbody>
        <tfoot className="statement-total">
          <tr>
            <th colSpan={3}>Total</th>
            <th className="text-end text-nowrap">{money(st.totalDebit)}</th>
            <th className="text-end text-nowrap">{money(st.totalCredit)}</th>
            <th />
          </tr>
          <tr>
            <th colSpan={5}>Closing balance</th>
            <th className="text-end text-nowrap">{drCr(st.closing)}</th>
          </tr>
        </tfoot>
      </Table>
    </>
  );
}

function RegistersView({ period, periodText }) {
  const [busy, setBusy] = useState('');
  const download = async (key, url, name) => {
    try {
      setBusy(key);
      const response = await axios.get(url, { responseType: 'blob' });
      saveBlobResponse(response, name);
    } catch (error) {
      toast.error('Could not download the register');
    } finally {
      setBusy('');
    }
  };
  const range = `from=${period.from}&to=${period.to}`;
  return (
    <Card>
      <Card.Body>
        <p className="mb-3">Line by line, with HSN, GST rate and tax, for {periodText}. They open in Excel.</p>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="outline-primary" disabled={Boolean(busy)}
            onClick={() => download('sales', `/api/sale-bills/export?${range}`, `sales-register-${period.from}-to-${period.to}.csv`)}>
            {busy === 'sales' ? 'Preparing...' : 'Sales register'}
          </Button>
          <Button variant="outline-primary" disabled={Boolean(busy)}
            onClick={() => download('purchase', `/api/financial-statements/purchase-register?${range}`, `purchase-register-${period.from}-to-${period.to}.csv`)}>
            {busy === 'purchase' ? 'Preparing...' : 'Purchase register'}
          </Button>
        </div>
        <ul className="small text-muted mt-3 mb-0">
          <li><strong>Sales register:</strong> issued sale bills by bill date, then credit notes (negative) and debit notes.</li>
          <li><strong>Purchase register:</strong> vendor bills and non-GST slips by the vendor&apos;s bill date (as GSTR-2B lists
            them, with the date each was entered alongside), then debit notes raised on vendors (negative).</li>
        </ul>
      </Card.Body>
    </Card>
  );
}

function TrialBalanceView({ tb, money, drCr, onLedger }) {
  const tallies = Math.abs(Number(tb.debit) - Number(tb.credit)) < 0.005
    && Math.abs(Number(tb.closingDebit) - Number(tb.closingCredit)) < 0.005;
  return (
    <>
      <div className="mb-2">
        {tallies
          ? <Badge bg="success">Debits and credits tally</Badge>
          : <Badge bg="warning" text="dark">Closing balances differ by {money(Math.abs(Number(tb.closingDebit) - Number(tb.closingCredit)))} (opening balances)</Badge>}
      </div>
      <Table size="sm" bordered hover responsive className="statement-table">
        <thead className="statement-head">
          <tr><th>Ledger</th><th>Group</th><th className="text-end">Opening</th><th className="text-end">Debit</th>
            <th className="text-end">Credit</th><th className="text-end">Closing</th></tr>
        </thead>
        <tbody>
          {tb.rows.map((r) => (
            <tr key={r.ledgerId}>
              <td>
                <button type="button" className="btn btn-link btn-sm p-0 text-start text-decoration-none d-print-none"
                  title="Open this ledger's statement" onClick={() => onLedger(r.ledgerId)}>{r.name}</button>
                <span className="d-none d-print-inline">{r.name}</span>
              </td>
              <td className="small text-muted">{r.group}</td>
              <td className="text-end text-nowrap">{drCr(r.opening)}</td>
              <td className="text-end text-nowrap">{Number(r.debit) ? money(r.debit) : '-'}</td>
              <td className="text-end text-nowrap">{Number(r.credit) ? money(r.credit) : '-'}</td>
              <td className="text-end text-nowrap fw-semibold">{drCr(r.closing)}</td>
            </tr>
          ))}
          {tb.rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted">No ledger has a balance or entries in this period.</td></tr>}
        </tbody>
        <tfoot className="statement-total">
          <tr>
            <th colSpan={2}>Total</th>
            <th className="text-end text-nowrap small">{money(tb.openingDebit)} Dr<br />{money(tb.openingCredit)} Cr</th>
            <th className="text-end text-nowrap">{money(tb.debit)}</th>
            <th className="text-end text-nowrap">{money(tb.credit)}</th>
            <th className="text-end text-nowrap small">{money(tb.closingDebit)} Dr<br />{money(tb.closingCredit)} Cr</th>
          </tr>
        </tfoot>
      </Table>
    </>
  );
}

function DayBookView({ book, money, page, setPage }) {
  return (
    <>
      <Table size="sm" bordered hover responsive className="statement-table">
        <thead className="statement-head">
          <tr><th>Date</th><th>Reference</th><th>Entry</th><th>Debit</th><th>Credit</th><th className="text-end">Amount</th></tr>
        </thead>
        <tbody>
          {book.entries.map((e) => (
            <tr key={e.id}>
              <td className="text-nowrap">{showDate(e.date)}</td>
              <td className="text-nowrap">{e.reference}</td>
              <td className="small" title={e.description || ''}>{typeLabel(e.type)}</td>
              <td className="small">{e.debitLedger}</td>
              <td className="small">{e.creditLedger}</td>
              <td className="text-end text-nowrap">{money(e.amount)}</td>
            </tr>
          ))}
          {book.entries.length === 0 && <tr><td colSpan={6} className="text-center text-muted">No entries in this period.</td></tr>}
        </tbody>
      </Table>
      {book.pages > 1 && (
        <div className="d-flex align-items-center gap-2 d-print-none">
          <Button size="sm" variant="outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="small text-muted">Page {page + 1} of {book.pages} ({book.total} entries)</span>
          <Button size="sm" variant="outline-secondary" disabled={page + 1 >= book.pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </>
  );
}

export default FinancialStatements;
